import React, { useState } from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholder?: string;
  onLoad?: () => void;
  onError?: () => void;
  objectFit?: 'cover' | 'contain' | 'fill' | 'scale-down';
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className = '',
  placeholder,
  onLoad,
  onError,
  objectFit = 'cover'
}) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
    console.log(`Image loaded successfully: ${src}`);
    if (onLoad) onLoad();
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
    console.error(`Image failed to load: ${src}`);
    if (onError) onError();
  };

  // 安全的字符串编码函数，处理非Latin1字符
  const safeBase64Encode = (str: string): string => {
    try {
      // 先用encodeURIComponent编码非ASCII字符，然后用btoa编码
      return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) => {
        return String.fromCharCode(parseInt(p1, 16));
      }));
    } catch (error) {
      // 如果仍然失败，使用基本的ASCII字符串
      return btoa('Smart Library Product');
    }
  };

  // 生成默认占位符，使用安全的alt文本
  const safeAltText = alt.replace(/[^\x00-\x7F]/g, ''); // 移除非ASCII字符
  const displayAltText = safeAltText || 'Smart Library Product';
  
  const defaultPlaceholder = placeholder || `data:image/svg+xml;base64,${safeBase64Encode(`
    <svg width="600" height="400" xmlns="http://www.w3.org/2000/svg">
      <rect width="100%" height="100%" fill="#f3f4f6"/>
      <rect x="50" y="50" width="500" height="300" fill="white" stroke="#e5e7eb" stroke-width="2" rx="8"/>
      <text x="300" y="180" font-family="Arial, sans-serif" font-size="18" fill="#374151" text-anchor="middle">
        ${displayAltText}
      </text>
      <text x="300" y="210" font-family="Arial, sans-serif" font-size="14" fill="#6b7280" text-anchor="middle">
        Smart Library Product
      </text>
      <text x="300" y="240" font-family="Arial, sans-serif" font-size="12" fill="#9ca3af" text-anchor="middle">
        Image Loading...
      </text>
    </svg>
  `)}`;

  // 如果没有src或者src为空，直接显示占位符
  if (!src || src === '') {
    console.warn('OptimizedImage: Empty or invalid src provided');
    return (
      <div className={`relative overflow-hidden ${className}`}>
        <img
          src={defaultPlaceholder}
          alt={alt}
          className={`w-full h-full object-${objectFit}`}
        />
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 bg-gray-100 animate-pulse flex items-center justify-center z-10">
          <div className="text-gray-400 text-sm">Loading...</div>
        </div>
      )}
      
      <img
        src={hasError ? defaultPlaceholder : src}
        alt={alt}
        className={`w-full h-full object-${objectFit} transition-opacity duration-300 ${
          isLoading ? 'opacity-0' : 'opacity-100'
        }`}
        onLoad={handleLoad}
        onError={handleError}
        loading="lazy"
      />
      
      {hasError && (
        <div className="absolute top-2 left-2 bg-red-100 text-red-600 text-xs px-2 py-1 rounded">
          Image Error
        </div>
      )}
    </div>
  );
};

export default OptimizedImage; 